import { useCallback, useEffect, useState } from "react";
import { VerificationBadge } from "../components/VerificationBadge.js";

/**
 * The player's own account: what is verified, and how to verify what is not.
 *
 * Phone verification is by SMS. A messaging app is never required — many
 * players do not have one and do not want one, and a tournament aiming to
 * attract players in the USA and West Africa cannot make an app a condition
 * of entry.
 */

interface Me {
  nickname: string;
  publicId: string;
  firstName: string;
  lastName: string;
  country: string | null;
  fmjdId: string | null;
  email: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  verificationLevel?: "none" | "contact" | "identity";
}

type PhoneStep =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "code"; destination: string }
  | { kind: "done" }
  | { kind: "error"; message: string };

export function Account({ onChange }: { onChange?: () => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [smsLive, setSmsLive] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    const res = await fetch("/api/auth/me");
    if (res.ok) setMe(await res.json());
    onChange?.();
  }, [onChange]);

  useEffect(() => {
    void refresh();
    void fetch("/api/verify/phone/channels")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSmsLive(d?.live ?? false))
      .catch(() => setSmsLive(false));
  }, [refresh]);

  if (!me) return <p style={{ color: "#666" }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 560 }}>
      <h1 style={{ marginTop: 0, fontSize: 24 }}>Your account</h1>

      <table style={{ fontSize: 14, borderCollapse: "collapse", marginBottom: 28 }}>
        <tbody>
          <Row label="Nickname" value={me.nickname} />
          <Row label="Name" value={`${me.firstName} ${me.lastName}`} />
          <Row label="Country" value={me.country ?? "—"} />
          <Row label="FMJD ID" value={me.fmjdId ?? "not linked"} />
          <Row
            label="Verification"
            value={<VerificationBadge level={me.verificationLevel ?? "none"} />}
          />
        </tbody>
      </table>

      <Section
        title="Email address"
        done={me.emailVerified}
        doneText={`${me.email} — confirmed`}
      >
        <EmailVerify email={me.email} onDone={refresh} />
      </Section>

      <Section
        title="Mobile telephone"
        done={me.phoneVerified}
        doneText="Confirmed"
      >
        <PhoneVerify smsLive={smsLive} onDone={refresh} />
      </Section>

      <Section title="FMJD ID" done={Boolean(me.fmjdId)} doneText={`Linked: ${me.fmjdId}`}>
        <FmjdLink onDone={refresh} />
      </Section>

      {me.verificationLevel === "contact" && (
        <div
          style={{
            background: "#e0f2f1",
            border: "1px solid #80cbc4",
            borderRadius: 8,
            padding: 14,
            fontSize: 14,
          }}
        >
          <strong>You are a verified player.</strong> This counts for every FMJD
          tournament — you will not have to do it again.
        </div>
      )}
    </div>
  );
}

function EmailVerify({ email, onDone }: { email: string; onDone: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);

  async function resend() {
    const res = await fetch("/api/verify/email/resend", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    setMsg(
      res.ok
        ? "Sent. Check your inbox."
        : data.retryAfterS
          ? `Please wait ${data.retryAfterS}s before requesting another.`
          : "Could not send, please try again later."
    );
    onDone();
  }

  return (
    <div>
      <p style={{ fontSize: 14, color: "#555", margin: "0 0 10px" }}>
        We sent a confirmation link to <strong>{email}</strong>.
      </p>
      {msg ? (
        <p style={{ fontSize: 14, color: "#00695c", margin: 0 }}>{msg}</p>
      ) : (
        <button onClick={resend} style={button}>
          Send it again
        </button>
      )}
    </div>
  );
}

function PhoneVerify({
  smsLive,
  onDone,
}: {
  smsLive: boolean | null;
  onDone: () => void;
}) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<PhoneStep>({ kind: "idle" });

  async function start() {
    setStep({ kind: "sending" });
    const res = await fetch("/api/verify/phone/start", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ channel: "sms", destination: phone.trim() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setStep({
        kind: "error",
        message:
          data.error === "too_soon"
            ? `Please wait ${data.retryAfterS}s before requesting another code.`
            : data.error === "channel_unavailable"
              ? "SMS is not available at the moment. Please try again later."
              : "Could not send the code. Check the number and try again.",
      });
      return;
    }
    setStep({ kind: "code", destination: phone.trim() });
  }

  async function confirm() {
    const res = await fetch("/api/verify/phone/confirm", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ channel: "sms", code: code.trim() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setStep({
        kind: "error",
        message:
          data.error === "expired"
            ? "That code has expired. Request a new one."
            : data.error === "too-many-attempts"
              ? "Too many attempts. Request a new code."
              : "That code is not right.",
      });
      return;
    }
    setStep({ kind: "done" });
    onDone();
  }

  if (step.kind === "done") {
    return <p style={{ color: "#2e7d32", fontSize: 14, margin: 0 }}>Telephone confirmed.</p>;
  }

  return (
    <div>
      {smsLive === false && (
        <div
          style={{
            background: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: 6,
            padding: 10,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          No SMS gateway is configured yet, so codes are written to the server log
          instead of being sent.
        </div>
      )}

      {step.kind !== "code" ? (
        <>
          <p style={{ fontSize: 14, color: "#555", margin: "0 0 8px" }}>
            We will send a six-digit code by SMS. No app is needed.
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+31612345678"
              style={{ ...input, flex: 1 }}
              autoComplete="tel"
            />
            <button
              onClick={start}
              disabled={step.kind === "sending" || phone.trim().length < 8}
              style={button}
            >
              {step.kind === "sending" ? "Sending…" : "Send code"}
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
            International format, starting with +
          </p>
        </>
      ) : (
        <>
          <p style={{ fontSize: 14, color: "#555", margin: "0 0 8px" }}>
            Enter the code sent to <strong>{step.destination}</strong>.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              style={{ ...input, width: 120, fontSize: 18, letterSpacing: 3 }}
              autoFocus
            />
            <button onClick={confirm} disabled={code.length !== 6} style={button}>
              Confirm
            </button>
            <button onClick={() => setStep({ kind: "idle" })} style={linkButton}>
              use a different number
            </button>
          </div>
        </>
      )}

      {step.kind === "error" && (
        <p style={{ color: "#c62828", fontSize: 14, margin: "10px 0 0" }}>{step.message}</p>
      )}
    </div>
  );
}

/**
 * Linking an FMJD identity.
 *
 * Two steps: the player enters their ID, the platform shows the name FMJD
 * holds, and the player confirms it is them. The player never types their own
 * name for this purpose — FMJD is the master record, so there is no name
 * matching to get wrong.
 */
function FmjdLink({ onDone }: { onDone: () => void }) {
  const [fmjdId, setFmjdId] = useState("");
  const [found, setFound] = useState<{
    fmjdId: string;
    name: string;
    country: string | null;
    rating: number | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<boolean | null>(null);

  useEffect(() => {
    void fetch("/api/fmjd/status")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setLive(d?.live ?? false))
      .catch(() => setLive(false));
  }, []);

  async function lookup() {
    setBusy(true);
    setError(null);
    setFound(null);
    const res = await fetch("/api/fmjd/lookup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ fmjdId: fmjdId.trim() }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) {
      setError(data.message ?? "Could not look up that ID.");
      return;
    }
    setFound(data);
  }

  async function confirm() {
    if (!found) return;
    setBusy(true);
    const res = await fetch("/api/fmjd/link", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ fmjdId: found.fmjdId }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) {
      setError(data.message ?? "Could not link that ID.");
      return;
    }
    onDone();
  }

  return (
    <div>
      {live === false && (
        <div
          style={{
            background: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: 6,
            padding: 10,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          The FMJD database is not connected yet, so a small test list is used.
          Try 10018, 10001, 10002 or 10003.
        </div>
      )}

      {!found ? (
        <>
          <p style={{ fontSize: 14, color: "#555", margin: "0 0 8px" }}>
            Enter your FMJD ID. We will show the name FMJD holds so you can confirm
            it is you.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={fmjdId}
              onChange={(e) => setFmjdId(e.target.value.replace(/\D/g, "").slice(0, 16))}
              placeholder="10018"
              style={{ ...input, width: 140 }}
              inputMode="numeric"
            />
            <button onClick={lookup} disabled={busy || fmjdId.trim().length === 0} style={button}>
              {busy ? "Looking up…" : "Look up"}
            </button>
          </div>
        </>
      ) : (
        <div>
          <p style={{ fontSize: 14, color: "#555", margin: "0 0 8px" }}>Is this you?</p>
          <div
            style={{
              background: "#f5f5f5",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 10,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 15 }}>{found.name}</div>
            <div style={{ fontSize: 13, color: "#666" }}>
              FMJD {found.fmjdId}
              {found.country && ` · ${found.country}`}
              {found.rating && ` · rating ${found.rating}`}
            </div>
          </div>
          <button onClick={confirm} disabled={busy} style={button}>
            {busy ? "Linking…" : "Yes, this is me"}
          </button>{" "}
          <button onClick={() => setFound(null)} style={linkButton}>
            no, try another ID
          </button>
          <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
            Your name and country will be taken from the FMJD database.
          </p>
        </div>
      )}

      {error && <p style={{ color: "#c62828", fontSize: 14, margin: "10px 0 0" }}>{error}</p>}
    </div>
  );
}

function Section({
  title,
  done,
  doneText,
  children,
}: {
  title: string;
  done: boolean;
  doneText: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: 16,
        marginBottom: 14,
        background: done ? "#f6fbf6" : "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: done ? 0 : 10 }}>
        <strong style={{ fontSize: 15 }}>{title}</strong>
        {done && (
          <span style={{ fontSize: 12, color: "#2e7d32", fontWeight: 600 }}>✓ {doneText}</span>
        )}
      </div>
      {!done && children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ color: "#777", padding: "5px 16px 5px 0" }}>{label}</td>
      <td style={{ padding: "5px 0", fontWeight: 500 }}>{value}</td>
    </tr>
  );
}

const input: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid #ccc",
  borderRadius: 6,
  boxSizing: "border-box",
};

const button: React.CSSProperties = {
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  background: "#00695c",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
};

const linkButton: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#00695c",
  fontSize: 13,
  cursor: "pointer",
  textDecoration: "underline",
};
